import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { toast } from 'sonner';

import { useServerPersistence } from '@/hooks/useServerPersistence';
import { useSimulatorState } from '@/store/useSimulatorState';

import { AppShell } from '@/components/AppShell';
import { ChartsArea } from '@/components/charts/ChartsArea';
import { ConfigSummary } from '@/components/ConfigSummary';
import { ConfigQuickLinks } from '@/components/ConfigQuickLinks';
import { Header } from '@/components/Header';
import { ConfigDrawer } from '@/components/panels/ConfigDrawer';
import { KpiCardsRow } from '@/components/kpi/KpiCardsRow';
import { ScenarioHeading } from '@/components/ScenarioHeading';

export function SimulationPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { simulationId, isLoading, lastError, loadSimulation } = useSimulatorState();
  useServerPersistence();

  const [configOpen, setConfigOpen] = useState(false);
  const [configTab, setConfigTab] = useState<string>('info');

  const openConfigTab = (tab: string) => {
    setConfigTab(tab);
    setConfigOpen(true);
  };

  useEffect(() => {
    if (!id || id === simulationId) return;
    loadSimulation(id);
  }, [id, simulationId, loadSimulation]);

  useEffect(() => {
    if (lastError && !isLoading) {
      toast.error(lastError);
      navigate('/');
    }
  }, [lastError, isLoading, navigate]);

  if (isLoading || simulationId !== id) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Chargement…
      </div>
    );
  }

  return (
    <AppShell>
      <main className="flex flex-col gap-6 p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <ScenarioHeading />
          <Header onOpenConfig={() => openConfigTab('info')} />
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
          <div className="flex flex-col gap-4">
            <KpiCardsRow />
            <ChartsArea />
            <ConfigSummary />
          </div>
          <div className="print:hidden">
            <ConfigQuickLinks onOpenTab={openConfigTab} />
          </div>
        </div>
      </main>

      <ConfigDrawer open={configOpen} onOpenChange={setConfigOpen} defaultTab={configTab} />
    </AppShell>
  );
}
